import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Database, ref, onValue, get, set, update,
} from '@angular/fire/database';
import {
  Storage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject,
} from '@angular/fire/storage';
import { firstValueFrom } from 'rxjs';

// ── Types exports ─────────────────────────────────────────────────────────────

export interface CarouselSlide {
  imageUrl: string;
  alt?: string;
  storagePath?: string;
}

export interface ThemeConfig {
  primaryColor: string;
  primaryDark: string;
  secondaryColor: string;
  fontFamily: string;
  heroImage?: string;
}

export interface ShopConfig {
  name: string;
  tagline: string;
  whatsapp: string;
  email: string;
  address: string;
  logoUrl: string;
  faviconUrl: string;
}

export const THEME_DEFAULTS: ThemeConfig = {
  primaryColor:   '#1abc9c',
  primaryDark:    '#148f77',
  secondaryColor: '#2c3e50',
  fontFamily:     'Roboto, "Helvetica Neue", sans-serif',
};

/** `#rrggbb` pour `<input type="color">` et sauvegardes RTDB stables. */
export function normalizeHexColor(value: string | undefined | null, fallback: string): string {
  if (value == null || typeof value !== 'string') return fallback;
  const raw = value.trim();
  if (!raw.startsWith('#')) return fallback;
  const body = raw.slice(1);
  if (/^[0-9a-fA-F]{6}$/.test(body)) return `#${body.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(body)) {
    const [r, g, b] = body.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export const SHOP_DEFAULTS: ShopConfig = {
  name:       'Délice Éternel',
  tagline:    'Prêt-à-porter africain',
  whatsapp:   '+24177601044',
  email:      '',
  address:    '',
  logoUrl:    'assets/style-sauvage_only_logo-removebg.png',
  faviconUrl: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function mergeDeep(target: any, ...sources: any[]): any {
  for (const source of sources) {
    if (!source) continue;
    for (const key of Object.keys(source)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key]) target[key] = {};
        mergeDeep(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  return target;
}

export function flattenTranslations(obj: any, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'string') {
      result[fullKey] = val;
    } else if (val && typeof val === 'object') {
      Object.assign(result, flattenTranslations(val, fullKey));
    }
  }
  return result;
}

export function unflattenTranslations(flat: Record<string, string>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, val] of Object.entries(flat)) {
    const parts = key.split('.');
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = val;
  }
  return result;
}

// ── Repository ────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AppConfigRepository {

  constructor(
    private db: Database,
    private storage: Storage,
    private http: HttpClient,
  ) {}

  // ── Translations ────────────────────────────────────────────────────────────

  getTranslationsOnce(lang: string): Promise<Record<string, any> | null> {
    return get(ref(this.db, `config/i18n/${lang}`)).then(snap => snap.val());
  }

  saveTranslations(lang: string, translations: Record<string, any>): Promise<void> {
    return set(ref(this.db, `config/i18n/${lang}`), translations);
  }

  loadStaticTranslations(lang: string): Promise<Record<string, any>> {
    return firstValueFrom(
      this.http.get<Record<string, any>>(`./assets/i18n/${lang}.json`).pipe(
        catchError(() => of({}))
      )
    );
  }

  // ── Carousel ────────────────────────────────────────────────────────────────

  watchCarousel(): Observable<CarouselSlide[]> {
    return new Observable(observer => {
      const unsubscribe = onValue(
        ref(this.db, 'config/carousel'),
        snap => {
          const val = snap.val();
          if (!val) { observer.next([]); return; }
          const slides: CarouselSlide[] = Array.isArray(val)
            ? val.filter(Boolean)
            : Object.values(val).filter(Boolean) as CarouselSlide[];
          observer.next(slides);
        },
        err => observer.error(err)
      );
      return () => unsubscribe();
    });
  }

  saveCarousel(slides: CarouselSlide[]): Promise<void> {
    return set(ref(this.db, 'config/carousel'), slides);
  }

  async uploadCarouselImage(file: File): Promise<CarouselSlide> {
    const path = `config/carousel/${Date.now()}_${file.name}`;
    const sRef = storageRef(this.storage, path);
    await uploadBytes(sRef, file);
    const imageUrl = await getDownloadURL(sRef);
    return { imageUrl, storagePath: path };
  }

  async deleteCarouselImage(slide: CarouselSlide): Promise<void> {
    if (slide.storagePath) {
      try {
        await deleteObject(storageRef(this.storage, slide.storagePath));
      } catch { /* ignoré si déjà supprimé */ }
    }
  }

  // ── App title (legacy — conservé pour compatibilité) ───────────────────────

  watchAppTitle(): Observable<{ fr: string; en: string }> {
    return new Observable(observer => {
      const unsubscribe = onValue(
        ref(this.db, 'config/appTitle'),
        snap => {
          const val = snap.val();
          observer.next(val ?? { fr: 'Délice Éternel', en: 'Délice Éternel' });
        },
        err => observer.error(err)
      );
      return () => unsubscribe();
    });
  }

  saveAppTitle(fr: string, en: string): Promise<void> {
    return set(ref(this.db, 'config/appTitle'), { fr, en });
  }

  // ── Labels configurables ─────────────────────────────────────────────────────

  watchComplementaryLookTitle(): Observable<{ fr: string; en: string }> {
    return new Observable(observer => {
      const unsubscribe = onValue(
        ref(this.db, 'config/labels/complementaryLookTitle'),
        snap => {
          const val = snap.val();
          observer.next(val ?? { fr: 'Vos suggestions', en: 'Your suggestions' });
        },
        err => observer.error(err)
      );
      return () => unsubscribe();
    });
  }

  saveComplementaryLookTitle(fr: string, en: string): Promise<void> {
    return set(ref(this.db, 'config/labels/complementaryLookTitle'), { fr, en });
  }

  // ── Theme ────────────────────────────────────────────────────────────────────

  watchTheme(): Observable<ThemeConfig> {
    return new Observable(observer => {
      const unsubscribe = onValue(
        ref(this.db, 'config/theme'),
        snap => observer.next({ ...THEME_DEFAULTS, ...(snap.val() ?? {}) }),
        err => observer.error(err),
      );
      return () => unsubscribe();
    });
  }

  saveTheme(theme: Partial<ThemeConfig>): Promise<void> {
    return update(ref(this.db, 'config/theme'), theme as Record<string, any>);
  }

  // ── Shop ─────────────────────────────────────────────────────────────────────

  watchShop(): Observable<ShopConfig> {
    return new Observable(observer => {
      const unsubscribe = onValue(
        ref(this.db, 'config/shop'),
        snap => observer.next({ ...SHOP_DEFAULTS, ...(snap.val() ?? {}) }),
        err => observer.error(err),
      );
      return () => unsubscribe();
    });
  }

  saveShop(shop: Partial<ShopConfig>): Promise<void> {
    return update(ref(this.db, 'config/shop'), shop as Record<string, any>);
  }

  // ── Logo / Favicon (Firebase Storage) ────────────────────────────────────────
  // Même flux que le template white-label (uploadBytes + getDownloadURL).
  // Si le navigateur signale une erreur CORS sur firebasestorage.googleapis.com,
  // configurer le bucket GCS : voir readme-docs/firebase-storage-cors.md et cors.firebase-storage.json à la racine du repo.

  async uploadLogo(file: File): Promise<string> {
    const sRef = storageRef(this.storage, 'config/branding/logo');
    await uploadBytes(sRef, file);
    return getDownloadURL(sRef);
  }

  async uploadFavicon(file: File): Promise<string> {
    const sRef = storageRef(this.storage, 'config/branding/favicon');
    await uploadBytes(sRef, file);
    return getDownloadURL(sRef);
  }
}
